import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { Turnstile } from '@marsidev/react-turnstile'

interface ForgotPasswordDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ForgotPasswordDialog = ({ isOpen, onClose }: ForgotPasswordDialogProps) => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();
  const [captchaToken, setCaptchaToken] = useState();

  const devSiteKey  = "1x00000000000000000000AA";
  const prodSiteKey = "0x4AAAAAABipt3BbaWslaZ-z";

  let siteKey = "";
  if (!window.location.hostname.includes("jitume.gebeya.com")) {
    siteKey = devSiteKey;
  } else {
    siteKey = prodSiteKey; 
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        variant: "destructive",
        title: t('common.error'),
        description: t('auth.pleaseEnterEmail'),
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?tab=reset-password`,
        captchaToken: captchaToken
      });

      if (error) {
        throw error;
      }

      setIsSuccess(true);
      toast({
        title: t('auth.passwordResetEmailSent'),
        description: t('auth.checkInboxForInstructions'),
      });
    } catch (error: any) {
      console.error("Password reset error:", error);
      toast({
        variant: "destructive",
        title: t('common.error'),
        description: error.message || t('auth.failedToSendResetEmail'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setIsSuccess(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('auth.resetYourPassword')}</DialogTitle>
          <DialogDescription>
            {!isSuccess 
              ? t('auth.enterEmailForReset')
              : t('auth.checkEmailForReset')}
          </DialogDescription>
        </DialogHeader>

        {!isSuccess ? (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-email">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="reset-email"
                  type="email"
                  placeholder={t('auth.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex justify-center">
              <Turnstile
                siteKey={siteKey}
                onSuccess={(token: any) => setCaptchaToken(token)}
                onError={() => setCaptchaToken(null)}
              />
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleClose}
                disabled={isLoading}
              >
                {t('common.cancel')}
              </Button>
  
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90" 
                disabled={isLoading || !captchaToken}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <img 
                      src="/lovable-uploads/f8714beb-a432-4da6-acab-3ef6f0a42367.png" 
                      alt={t('common.loading')} 
                      className="h-4 w-4 gebeya-spinner" 
                    />
                    <span>{t('auth.sending')}</span>
                  </div>
                ) : t('auth.sendResetLink')}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col items-center justify-center p-4">
              <CheckCircle2 className="h-12 w-12 text-green-500 mb-2" />
              <p className="text-center">
                {t('auth.resetLinkSentTo')} <strong>{email}</strong>. 
                {t('auth.checkInboxAndFollow')}
              </p>
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                onClick={handleClose} 
                className="w-full bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90"
              >
                {t('common.close')}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
